import { Suspense, useState } from 'react';

import { useGetApplication } from '../../api/lending/lapiHooks';
import { CustomErrorBoundary } from '../../AppWrapper';
import { LoadingModal } from '../../components/LoadingModal/LoadingModal';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { AccountContentSkeleton } from './components/AccountContentSkeleton/AccountContentSkeleton';
import { AccountSections } from './components/AccountSections/AccountSections';
import { AccountSpendingLimitDrawer } from './components/AccountSpendingLimitDrawer/AccountSpendingLimitDrawer';
import { BuyerCrossSellCard } from './components/BuyerCrossSellCard/BuyerCrossSellCard';
import { IsANTCard } from './components/IsANTCard/IsANTCard';
import { IsOverdueBanner } from './components/IsOverdueBanner';
import { PostCheckoutGuard } from './components/PostCheckoutGuard/PostCheckoutGuard';
import {
  SellerDirectoryBanners,
  SellerDirectoryBannersSkeleton,
} from './components/SellerDirectoryBanner/SellerDirectoryBanners';
import { SignupGuard } from './components/SignupGuard/SignupGuard';
import { SLSecurityCheckBanner } from './components/SLSecurityCheckBanner/SLSecurityCheckBanner';
import {
  SpendingLimitCard,
  SpendingLimitCardSkeleton,
} from './components/SpendingLimitCard/SpendingLimitCard';
import {
  WelcomeErrorFallback,
  WelcomeMessage,
  WelcomeMessageSkeleton,
} from './components/WelcomeMessage/WelcomeMessage';
import { useRefreshSpendingLimits } from './hooks/useRefreshSpendingLimits';

export function Account() {
  const { fetchApplication } = useGetApplication();
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const [isSpendingLimitDrawerOpen, setIsSpendingLimitDrawerOpen] =
    useState(false);

  const {
    refreshSpendingLimit,
    isLoading: loadingRefreshSpendingLimit,
    errorMessage: refreshSpendingLimitErrorMessage,
  } = useRefreshSpendingLimits(() => {
    fetchApplication();
  });

  const handleRefreshSpendingLimit = async () => {
    try {
      await refreshSpendingLimit();
    } catch {
      // do nothing
    }
  };

  // These are the possible banners that can be shown on the account page,
  // in order of visibility.
  const dashboardBanners = [
    IsOverdueBanner,
    IsANTCard,
    SLSecurityCheckBanner,
    BuyerCrossSellCard,
  ];

  return (
    <>
      <CustomErrorBoundary errorComponent={<></>}>
        <Suspense>
          <AccountSpendingLimitDrawer
            isSpendingLimitDrawerOpen={isSpendingLimitDrawerOpen}
            setIsSpendingLimitDrawerOpen={setIsSpendingLimitDrawerOpen}
            loadingRefreshSpendingLimit={loadingRefreshSpendingLimit}
            refreshSpendingLimitErrorMessage={refreshSpendingLimitErrorMessage}
            handleRefreshSpendingLimit={handleRefreshSpendingLimit}
          />
        </Suspense>
      </CustomErrorBoundary>
      <div className="min-h-[calc(100vh-72px)] bg-structure-100 pb-2xl pt-[60px] text-left font-reg">
        <div
          className="mx-auto grid max-w-[1136px] grid-cols-[350px_1fr] gap-x-3xl m:grid-cols-1"
          style={{
            gridTemplateAreas: isMobile
              ? "'welcome' 'application' 'banners' 'plans'"
              : "'welcome welcome' 'banners banners' 'application plan' 'plans plan'",
            gridTemplateRows: 'max-content max-content max-content 1fr',
          }}
        >
          <section className="mb-xl text-left" style={{ gridArea: 'welcome' }}>
            <div className="grid grid-cols-1 gap-m">
              {dashboardBanners.map((Banner) => (
                <CustomErrorBoundary errorComponent={<></>} key={Banner.name}>
                  <Suspense fallback={<></>}>
                    <Banner />
                  </Suspense>
                </CustomErrorBoundary>
              ))}
            </div>
            <CustomErrorBoundary errorComponent={<WelcomeErrorFallback />}>
              <Suspense fallback={<WelcomeMessageSkeleton />}>
                <WelcomeMessage />
              </Suspense>
            </CustomErrorBoundary>
            <CustomErrorBoundary errorComponent={<></>}>
              <Suspense fallback={<SpendingLimitCardSkeleton />}>
                <SpendingLimitCard
                  setIsSpendingLimitDrawerOpen={setIsSpendingLimitDrawerOpen}
                  refreshSpendingLimit={handleRefreshSpendingLimit}
                  loadingRefreshSpendingLimit={loadingRefreshSpendingLimit}
                />
              </Suspense>
            </CustomErrorBoundary>
          </section>
          <section
            className="mb-xl flex flex-row gap-xl sm:flex-col"
            style={{ gridArea: 'banners' }}
          >
            <CustomErrorBoundary errorComponent={<></>}>
              <Suspense fallback={<SellerDirectoryBannersSkeleton />}>
                <SellerDirectoryBanners />
              </Suspense>
            </CustomErrorBoundary>
          </section>
          <CustomErrorBoundary errorComponent={<></>}>
            <Suspense
              fallback={
                <>
                  <LoadingModal
                    isOpen={true}
                    title="We’re organising your payment plans into one clear view"
                    subText="You’ve taken control and spread the cost of multiple payments - give us a moment while we bring it all together."
                  />
                  <AccountContentSkeleton isMobile={isMobile} />
                </>
              }
            >
              <SignupGuard>
                <PostCheckoutGuard isMobile={isMobile}>
                  <AccountSections
                    isMobile={isMobile}
                    setIsSpendingLimitDrawerOpen={setIsSpendingLimitDrawerOpen}
                    loadingRefreshSpendingLimit={loadingRefreshSpendingLimit}
                    handleRefreshSpendingLimit={handleRefreshSpendingLimit}
                  />
                </PostCheckoutGuard>
              </SignupGuard>
            </Suspense>
          </CustomErrorBoundary>
        </div>
      </div>
    </>
  );
}
